import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { OwnerPrices, PriceListGrid, PriceWriteResult } from '../../models/price-list';

/**
 * The central Price List (multi-currency Phase 2) — the one screen a non-base price is entered on.
 *
 * Before it, a ZiG price could only reach the system through a seeding command, so nothing could be priced and
 * the grid's `blocking` count could never reach zero — which is the precondition for ZiG being switched on at
 * all. No maths happens here: the API decides every figure, every state, and every refusal.
 */
@Injectable({
  providedIn: 'root',
})
export class PriceListService {
  currentDomain = environment.currentApiHostname;
  apiUrl = `https://${this.currentDomain}/api/general`;

  constructor(private http: HttpClient) {}

  /** The whole grid. `currencies` narrows it; the base is always included whatever is asked for. */
  grid(currencies?: string[]) {
    const query =
      currencies && currencies.length ? `?currencies=${encodeURIComponent(currencies.join(','))}` : '';

    return this.http.get<PriceListGrid>(`${this.apiUrl}/price-list${query}`, this.authOptions());
  }

  /**
   * ONE record's prices, for the screen that owns it — so a ZiG price is typed where the USD one is.
   *
   * The two screens cannot drift: this is the same data the grid shows, not a second copy of it.
   */
  forOwner(ownerType: string, ownerUuid: string) {
    return this.http.get<OwnerPrices>(
      `${this.apiUrl}/price-list/owner/${encodeURIComponent(ownerType)}/${encodeURIComponent(ownerUuid)}`,
      this.authOptions()
    );
  }

  /**
   * Set one non-base price.
   *
   * `amount` is sent EXPLICITLY, null included: null means "this record has no such charge" and bills zero,
   * which is not the same as having no price at all (that refuses the charge). The API rejects a payload with
   * the key missing, on purpose — so a dropped form field can never be read as "bills nothing".
   */
  set(payload: {
    owner_type: string;
    owner_uuid: string;
    currency: string;
    field: string;
    amount: number | null;
  }) {
    return this.http.post<PriceWriteResult>(`${this.apiUrl}/price-list`, payload, this.authOptions());
  }

  private authOptions() {
    return {
      headers: new HttpHeaders({
        Accept: 'application/json',
        Authorization: 'Bearer ' + localStorage.getItem('access_token'),
      }),
    };
  }
}
